import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useBranding } from '../store/branding';
import * as onboardingApi from '../api/onboarding';
import type {
  OnboardingState,
  OnboardingStepName,
  EmergencyContactPayload,
} from '../api/onboarding';
import LoadingState from '../components/ui/LoadingState';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const STEP_ORDER: OnboardingStepName[] = ['PERSONAL_INFO', 'EMERGENCY_CONTACTS', 'PDPA_CONSENT'];
const STEP_TITLES: Record<OnboardingStepName, string> = {
  PERSONAL_INFO: 'Your details',
  EMERGENCY_CONTACTS: 'Emergency contacts & family',
  PDPA_CONSENT: 'Privacy consent',
};

const fieldClass =
  'flex h-11 w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const labelClass = 'mb-1.5 block text-sm font-medium text-foreground';

// ---- Step 1: Personal Info -----------------------------------------------

interface PersonalInfoFormProps {
  prefill: OnboardingState['prefill']['personalInfo'];
  saving: boolean;
  onSubmit: (data: onboardingApi.PersonalInfoPayload) => void;
  onSkip: () => void;
}

function PersonalInfoForm({ prefill, saving, onSubmit, onSkip }: PersonalInfoFormProps) {
  const [phoneNumber, setPhoneNumber] = useState(prefill.phoneNumber ?? '');
  const [address1, setAddress1] = useState(prefill.address1 ?? '');
  const [address2, setAddress2] = useState(prefill.address2 ?? '');
  const [city, setCity] = useState(prefill.city ?? '');
  const [postcode, setPostcode] = useState(prefill.postcode ?? '');
  const [state, setState] = useState(prefill.state ?? '');
  const [maritalStatus, setMaritalStatus] = useState(prefill.maritalStatus ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      phoneNumber: phoneNumber || undefined,
      address1: address1 || undefined,
      address2: address2 || undefined,
      city: city || undefined,
      postcode: postcode || undefined,
      state: state || undefined,
      maritalStatus: maritalStatus || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="phoneNumber" className={labelClass}>Phone number</label>
        <input
          id="phoneNumber"
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className={fieldClass}
          placeholder="+60 12-345 6789"
        />
      </div>
      <div>
        <label htmlFor="address1" className={labelClass}>Address line 1</label>
        <input
          id="address1"
          type="text"
          value={address1}
          onChange={(e) => setAddress1(e.target.value)}
          className={fieldClass}
          placeholder="Street address"
        />
      </div>
      <div>
        <label htmlFor="address2" className={labelClass}>Address line 2</label>
        <input
          id="address2"
          type="text"
          value={address2}
          onChange={(e) => setAddress2(e.target.value)}
          className={fieldClass}
          placeholder="Apartment, suite, etc. (optional)"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="city" className={labelClass}>City</label>
          <input
            id="city"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className={fieldClass}
            placeholder="Kuala Lumpur"
          />
        </div>
        <div>
          <label htmlFor="postcode" className={labelClass}>Postcode</label>
          <input
            id="postcode"
            type="text"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            className={fieldClass}
            placeholder="50000"
          />
        </div>
      </div>
      <div>
        <label htmlFor="state" className={labelClass}>State</label>
        <input
          id="state"
          type="text"
          value={state}
          onChange={(e) => setState(e.target.value)}
          className={fieldClass}
          placeholder="Selangor"
        />
      </div>
      <div>
        <label htmlFor="maritalStatus" className={labelClass}>Marital status</label>
        <select
          id="maritalStatus"
          value={maritalStatus}
          onChange={(e) => setMaritalStatus(e.target.value)}
          className={fieldClass}
        >
          <option value="">Select…</option>
          <option value="SINGLE">Single</option>
          <option value="MARRIED">Married</option>
          <option value="DIVORCED">Divorced</option>
          <option value="WIDOWED">Widowed</option>
          <option value="REGISTERED_PARTNERSHIP">Registered partnership</option>
        </select>
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onSkip} disabled={saving} className="flex-1">
          Skip for now
        </Button>
        <Button type="submit" disabled={saving} className="flex-1">
          {saving ? 'Saving…' : 'Save & continue'}
        </Button>
      </div>
    </form>
  );
}

// ---- Step 2: Emergency Contacts ------------------------------------------

let nextRowId = 1;

type EmergencyContactDraft = Omit<EmergencyContactPayload, 'relationship'> & {
  id: number;
  relationship: EmergencyContactPayload['relationship'] | '';
};

interface Dependent {
  id: number;
  fullName: string;
  dateOfBirth: string;
  gender: 'M' | 'F' | '';
  relationshipType: string;
}

interface EmergencyContactsFormProps {
  prefill: OnboardingState['prefill'];
  saving: boolean;
  onSubmit: (data: onboardingApi.EmergencyContactsPayload) => void;
  onSkip: () => void;
}

function EmergencyContactsForm({ prefill, saving, onSubmit, onSkip }: EmergencyContactsFormProps) {
  const [contacts, setContacts] = useState<EmergencyContactDraft[]>(() => {
    if (prefill.emergencyContacts.length > 0) {
      return prefill.emergencyContacts.map((c) => ({
        id: nextRowId++,
        fullName: c.fullName,
        relationship: c.relationship as EmergencyContactPayload['relationship'] | '',
        phoneNumber: c.phoneNumber,
        email: c.email ?? '',
      }));
    }
    return [{ id: nextRowId++, fullName: '', relationship: '', phoneNumber: '', email: '' }];
  });

  const [showSpouse, setShowSpouse] = useState(!!prefill.spouse);
  const [spouseFullName, setSpouseFullName] = useState(prefill.spouse?.fullName ?? '');
  const [spousePhone, setSpousePhone] = useState(prefill.spouse?.phoneNumber ?? '');
  const [spouseOccupation, setSpouseOccupation] = useState(prefill.spouse?.occupationStatus ?? '');

  const [showDependents, setShowDependents] = useState(prefill.dependents.length > 0);
  const [dependents, setDependents] = useState<Dependent[]>(() => {
    if (prefill.dependents.length > 0) {
      return prefill.dependents.map((d) => ({
        id: nextRowId++,
        fullName: d.fullName,
        dateOfBirth: d.dateOfBirth ?? '',
        gender: (d.gender ?? '') as 'M' | 'F' | '',
        relationshipType: d.relationshipType ?? '',
      }));
    }
    return [];
  });

  const updateContact = (index: number, field: keyof EmergencyContactDraft, value: string) => {
    setContacts((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const addContact = () => {
    setContacts((prev) => [...prev, { id: nextRowId++, fullName: '', relationship: '', phoneNumber: '', email: '' }]);
  };

  const removeContact = (index: number) => {
    if (contacts.length === 1) return;
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDependent = (index: number, field: keyof Dependent, value: string) => {
    setDependents((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  };

  const addDependent = () => {
    setDependents((prev) => [...prev, { id: nextRowId++, fullName: '', dateOfBirth: '', gender: '', relationshipType: '' }]);
  };

  const removeDependent = (index: number) => {
    setDependents((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validContacts = contacts
      .filter((c) => !!c.fullName && !!c.phoneNumber && !!c.relationship)
      .map(({ id: _id, ...c }): EmergencyContactPayload => ({
        ...c,
        relationship: c.relationship as EmergencyContactPayload['relationship'],
        email: c.email || undefined,
      }));

    const payload: onboardingApi.EmergencyContactsPayload = {
      contacts: validContacts,
    };

    if (showSpouse && spouseFullName) {
      payload.spouse = {
        fullName: spouseFullName,
        phoneNumber: spousePhone || undefined,
        occupationStatus: spouseOccupation || undefined,
      };
    }

    const validDependents = dependents.filter(
      (d) => !!d.fullName && !!d.dateOfBirth && (d.gender === 'M' || d.gender === 'F')
    );

    if (showDependents && validDependents.length > 0) {
      payload.dependents = validDependents
        .filter((d): d is Dependent & { gender: 'M' | 'F' } => d.gender === 'M' || d.gender === 'F')
        .map(({ id: _id, ...d }) => ({
          fullName: d.fullName,
          dateOfBirth: d.dateOfBirth,
          gender: d.gender,
          relationshipType: d.relationshipType || undefined,
        }));
    }

    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Emergency contacts */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">Emergency contacts</p>
        {contacts.map((contact, index) => (
          <div key={contact.id} className="rounded-xl border border-border bg-muted/30 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Contact {index + 1}
              </span>
              {contacts.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeContact(index)}
                  className="text-xs text-destructive hover:underline"
                >
                  Remove
                </button>
              )}
            </div>
            <div>
              <label className={labelClass}>Full name *</label>
              <input
                type="text"
                value={contact.fullName}
                onChange={(e) => updateContact(index, 'fullName', e.target.value)}
                className={fieldClass}
                required
                placeholder="Full name"
              />
            </div>
            <div>
              <label className={labelClass}>Relationship *</label>
              <select
                value={contact.relationship}
                onChange={(e) => updateContact(index, 'relationship', e.target.value)}
                className={fieldClass}
                required
              >
                <option value="">Select…</option>
                <option value="SPOUSE">Spouse</option>
                <option value="PARENT">Parent</option>
                <option value="SIBLING">Sibling</option>
                <option value="CHILD">Child</option>
                <option value="FRIEND">Friend</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Phone number *</label>
              <input
                type="tel"
                value={contact.phoneNumber}
                onChange={(e) => updateContact(index, 'phoneNumber', e.target.value)}
                className={fieldClass}
                required
                placeholder="+60 12-345 6789"
              />
            </div>
            <div>
              <label className={labelClass}>Email (optional)</label>
              <input
                type="email"
                value={contact.email ?? ''}
                onChange={(e) => updateContact(index, 'email', e.target.value)}
                className={fieldClass}
                placeholder="email@example.com"
              />
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addContact}
          className="text-sm font-medium text-primary hover:underline"
        >
          + Add another contact
        </button>
      </div>

      {/* Spouse section */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setShowSpouse((v) => !v)}
          className="flex w-full items-center justify-between rounded-xl border border-border bg-muted/30 px-3 py-2.5 text-sm font-medium text-foreground"
        >
          <span>Spouse information</span>
          <span className="text-muted-foreground text-xs">{showSpouse ? 'Hide' : 'Add'}</span>
        </button>
        {showSpouse && (
          <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-3">
            <div>
              <label className={labelClass}>Full name *</label>
              <input
                type="text"
                value={spouseFullName}
                onChange={(e) => setSpouseFullName(e.target.value)}
                className={fieldClass}
                placeholder="Spouse's full name"
              />
            </div>
            <div>
              <label className={labelClass}>Phone number</label>
              <input
                type="tel"
                value={spousePhone}
                onChange={(e) => setSpousePhone(e.target.value)}
                className={fieldClass}
                placeholder="+60 12-345 6789"
              />
            </div>
            <div>
              <label className={labelClass}>Occupation status</label>
              <select
                value={spouseOccupation}
                onChange={(e) => setSpouseOccupation(e.target.value)}
                className={fieldClass}
              >
                <option value="">Select…</option>
                <option value="EMPLOYED">Employed</option>
                <option value="SELF_EMPLOYED">Self-employed</option>
                <option value="HOMEMAKER">Homemaker</option>
                <option value="RETIRED">Retired</option>
                <option value="STUDENT">Student</option>
                <option value="UNEMPLOYED">Unemployed</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Children/dependents section */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => {
            setShowDependents((v) => {
              if (!v && dependents.length === 0) {
                setDependents([{ id: nextRowId++, fullName: '', dateOfBirth: '', gender: '', relationshipType: '' }]);
              }
              return !v;
            });
          }}
          className="flex w-full items-center justify-between rounded-xl border border-border bg-muted/30 px-3 py-2.5 text-sm font-medium text-foreground"
        >
          <span>Children / dependents</span>
          <span className="text-muted-foreground text-xs">{showDependents ? 'Hide' : 'Add'}</span>
        </button>
        {showDependents && (
          <div className="space-y-3">
            {dependents.map((dep, index) => (
              <div key={dep.id} className="rounded-xl border border-border bg-muted/30 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Dependent {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeDependent(index)}
                    className="text-xs text-destructive hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <div>
                  <label className={labelClass}>Full name *</label>
                  <input
                    type="text"
                    value={dep.fullName}
                    onChange={(e) => updateDependent(index, 'fullName', e.target.value)}
                    className={fieldClass}
                    required={showDependents}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className={labelClass}>Date of birth *</label>
                  <input
                    type="date"
                    value={dep.dateOfBirth}
                    onChange={(e) => updateDependent(index, 'dateOfBirth', e.target.value)}
                    className={fieldClass}
                    required={showDependents}
                  />
                </div>
                <div>
                  <label className={labelClass}>Gender *</label>
                  <select
                    value={dep.gender}
                    onChange={(e) => updateDependent(index, 'gender', e.target.value)}
                    className={fieldClass}
                    required={showDependents}
                  >
                    <option value="">Select…</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Relationship type (optional)</label>
                  <select
                    value={dep.relationshipType}
                    onChange={(e) => updateDependent(index, 'relationshipType', e.target.value)}
                    className={fieldClass}
                  >
                    <option value="">Select…</option>
                    <option value="BIOLOGICAL">Biological</option>
                    <option value="ADOPTED">Adopted</option>
                    <option value="STEP">Step</option>
                  </select>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addDependent}
              className="text-sm font-medium text-primary hover:underline"
            >
              + Add another dependent
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onSkip} disabled={saving} className="flex-1">
          Skip for now
        </Button>
        <Button type="submit" disabled={saving} className="flex-1">
          {saving ? 'Saving…' : 'Save & continue'}
        </Button>
      </div>
    </form>
  );
}

// ---- Step 3: PDPA Consent ------------------------------------------------

interface PdpaFormProps {
  saving: boolean;
  onSubmit: () => void;
}

function PdpaForm({ saving, onSubmit }: PdpaFormProps) {
  const [consented, setConsented] = useState(false);
  const { displayName } = useBranding();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!consented) return;
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm text-foreground leading-relaxed">
        I consent to {displayName} collecting and processing my personal data for employment
        administration purposes in accordance with the Personal Data Protection Act 2010.
      </div>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          id="pdpa-consent"
          type="checkbox"
          checked={consented}
          onChange={(e) => setConsented(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
        />
        <span className="text-sm text-foreground">
          I have read and agree to the above consent statement.
        </span>
      </label>
      <Button type="submit" disabled={saving || !consented} className="w-full">
        {saving ? 'Saving…' : 'I agree'}
      </Button>
    </form>
  );
}

// ---- Main wizard ---------------------------------------------------------

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { displayName, productName } = useBranding();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showIntro, setShowIntro] = useState(false);

  useEffect(() => {
    onboardingApi.getOnboarding().then((res) => {
      if (!res.data) {
        setLoadError(res.error ?? 'Failed to load onboarding');
        return;
      }
      setState(res.data);
      // Open the first pending step; failing that, let the user revisit a
      // skipped one ("skip for now" means later, not never).
      const firstOpen = res.data.steps.findIndex((s) => s.status === 'PENDING');
      const firstSkipped = res.data.steps.findIndex((s) => s.status === 'SKIPPED');
      const target = firstOpen !== -1 ? firstOpen : firstSkipped;
      if (target === -1) {
        // Everything completed — go straight to dashboard
        refreshUser().then(() => navigate('/dashboard', { replace: true }));
      } else {
        setStepIndex(target);
        // First-ever visit (nothing touched yet): explain why before asking.
        setShowIntro(res.data.steps.every((s) => s.status === 'PENDING'));
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = async () => {
    await refreshUser();
    navigate('/dashboard', { replace: true });
  };

  const advance = async (steps: onboardingApi.OnboardingStepState[]) => {
    const nextPending = steps.findIndex((s) => s.status === 'PENDING');
    if (nextPending === -1) {
      await finish();
    } else {
      setStepIndex(nextPending);
    }
  };

  const handlePersonalInfoSubmit = async (data: onboardingApi.PersonalInfoPayload) => {
    setStepError(null);
    setSaving(true);
    const res = await onboardingApi.savePersonalInfo(data);
    setSaving(false);
    if (res.data) {
      await advance(res.data.steps);
    } else {
      setStepError(res.error ?? 'Save failed');
    }
  };

  const handleEmergencyContactsSubmit = async (data: onboardingApi.EmergencyContactsPayload) => {
    setStepError(null);
    setSaving(true);
    const res = await onboardingApi.saveEmergencyContacts(data);
    setSaving(false);
    if (res.data) {
      await advance(res.data.steps);
    } else {
      setStepError(res.error ?? 'Save failed');
    }
  };

  const handlePdpaSubmit = async () => {
    setStepError(null);
    setSaving(true);
    const res = await onboardingApi.recordPdpaConsent();
    setSaving(false);
    if (res.data) {
      await advance(res.data.steps);
    } else {
      setStepError(res.error ?? 'Save failed');
    }
  };

  const handleSkip = async () => {
    setStepError(null);
    setSaving(true);
    const res = await onboardingApi.skipStep(STEP_ORDER[stepIndex]);
    setSaving(false);
    if (res.data) {
      await advance(res.data.steps);
    } else {
      setStepError(res.error ?? 'Skip failed');
    }
  };

  if (loadError) {
    return <div className="p-6 text-sm text-destructive">{loadError}</div>;
  }

  if (!state) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center p-6">
        <LoadingState label="Loading…" rows={3} />
      </div>
    );
  }

  const currentStepName = STEP_ORDER[stepIndex];
  const totalSteps = STEP_ORDER.length;

  if (showIntro) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center bg-muted/50 px-4 pb-safe pt-safe">
        <div className="w-full max-w-lg space-y-6">
          <Card>
            <CardContent className="space-y-4 p-6">
              <h1 className="text-xl font-semibold text-foreground">Welcome to {productName}</h1>
              <p className="text-sm text-muted-foreground">
                {displayName} has provided you with access to {productName}, the company's platform
                for managing leave, attendance, claims, and payslips.
              </p>
              <p className="text-sm text-muted-foreground">
                To complete your employee profile, you will be guided through three brief steps:
                confirming your personal details, providing emergency contact information, and
                acknowledging the privacy consent. This takes approximately two minutes. The first
                two steps may be completed at a later time if preferred.
              </p>
              <p className="text-sm text-muted-foreground">
                All information collected is used solely for employment administration purposes and
                is handled in accordance with the Personal Data Protection Act 2010.
              </p>
              <Button className="w-full" onClick={() => setShowIntro(false)}>
                Get started
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-muted/50 pb-safe pt-safe">
      <div className="mx-auto w-full max-w-lg px-4 py-6 space-y-6">
        {/* Progress indicator */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Step {stepIndex + 1} of {totalSteps}</span>
            <span>{Math.round(((stepIndex + 1) / totalSteps) * 100)}% complete</span>
          </div>
          <div className="flex gap-1.5">
            {STEP_ORDER.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i <= stepIndex ? 'bg-primary' : 'bg-border'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step header */}
        <div>
          <h1 className="text-lg font-semibold text-foreground">{STEP_TITLES[currentStepName]}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {currentStepName === 'PERSONAL_INFO' && 'Help us keep your details up to date.'}
            {currentStepName === 'EMERGENCY_CONTACTS' && 'Who should we contact in an emergency?'}
            {currentStepName === 'PDPA_CONSENT' && 'Review and accept our data processing terms.'}
          </p>
        </div>

        {/* Inline step error */}
        {stepError && (
          <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {stepError}
          </div>
        )}

        {/* Step form */}
        <Card>
          <CardContent className="p-4">
            {currentStepName === 'PERSONAL_INFO' && (
              <PersonalInfoForm
                prefill={state.prefill.personalInfo}
                saving={saving}
                onSubmit={handlePersonalInfoSubmit}
                onSkip={handleSkip}
              />
            )}
            {currentStepName === 'EMERGENCY_CONTACTS' && (
              <EmergencyContactsForm
                prefill={state.prefill}
                saving={saving}
                onSubmit={handleEmergencyContactsSubmit}
                onSkip={handleSkip}
              />
            )}
            {currentStepName === 'PDPA_CONSENT' && (
              <PdpaForm saving={saving} onSubmit={handlePdpaSubmit} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
